import React, { useContext, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { MetadataContent } from '@jutro/legacy/uiconfig';
import { useTranslator } from '@jutro/locale';
import { LocalDateUtil } from '@xengage/gw-portals-util-js';
import _ from 'lodash';
import { ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react';
import { MLDriverVehicleUtil } from 'ml-portals-util-react';
import { useModal } from '@jutro/components';
import { FormModalPopover, MLTooltipComponent } from 'ml-components-platform-react';
import { MLDriverChangeAddDriverPopup } from 'ml-capability-policychange-common-react';
import appConfig from 'app-config';

import metadata from './MLDriverChangeTablePopup.metadata.json5';
import messages from './MLDriverChangeTablePopup.messages';
import styles from './MLDriverChangeTablePopup.module.scss';

import { IntlContext } from '@jutro/legacy/locale';

const MLDriverChangeTablePopup = (props) => {
    const modalApi = useModal();
    const {
        submissionVM,
        driverType,
        drivers,
        onAddDriver,
        onEditDriver,
        onRemoveDriver,
        onCancel,
        viewModelService,
        authHeader,
        showProgressDialog,
        EndorsementService
    } = props;

    const isGPA = appConfig.portalCode === 'GPA';
    const isAMP = appConfig.portalCode === 'AMP';
    const isModernProduct = _.get(submissionVM, 'isModernProduct_Ext.value', false);
    const translator = useTranslator();
    const intl = useContext(IntlContext);
    const genderTypeList = viewModelService.productMetadata.get('pc').types.getTypelist('GenderType');
    const maritalStatusTypelist = viewModelService.productMetadata.get('pc').types.getTypelist('MaritalStatus');
    const relationTypeList = viewModelService.productMetadata.get('pc').types.getTypelist('InsuredRelationType_ML');
    const statusTypeList = viewModelService.productMetadata.get('pc').types.getTypelist('DriverStatusType_ML');

    const {
        getFullName
    } = MLDriverVehicleUtil(viewModelService);

    const getFormattedDateString = useCallback((gwDateVal) => {
        const date = LocalDateUtil.toMidnightDate(gwDateVal);
        return intl.formatDate(date, { year: 'numeric', month: '2-digit', day: '2-digit' });
    }, [intl]);

    const getHeaderContent = () => {
        if (driverType === 'household') {
            return (
                <>
                    <span>{isModernProduct ? translator(messages.nonDrivers) : translator(messages.householdMembers)}</span>
                    <MLTooltipComponent
                        tooltipHeader={isModernProduct ?  translator(messages.HowToChangeFromNonDriver) : translator(messages.HowToChangeFromHouseholdMember)}
                        tooltipContent={translator(messages.changeToRatedDriver)}
                        noWrapper
                    />
                </>
            )
        }
        return (
            <>
                <span>{translator(messages.excludedDrivers)}</span>
                {!isAMP ? 
                    <MLTooltipComponent
                        tooltipHeader={translator(messages.HowToChangeFromExcludedDriver)}
                        tooltipContent={isModernProduct ? translator(messages.changeFromExludedNonDriver) : translator(messages.changeFromExluded)}
                        noWrapper
                    /> : null
                }
            </>
        )
    };

    const getAddButtonLabel = () => {
        switch (driverType) {
            case 'rated':
                return translator(messages.addDriver);
            case 'household':
                return isModernProduct ? translator(messages.addNonDrivers) : translator(messages.addHouseholdMember);
            case 'excluded':
                return translator(messages.addExcludedDriver);
            default:
                break;
        }
    };

    const generateDriverOverrides = () => {
        if (drivers) {
            const getTypeKeyDescription = (typeKeyName) => {
                return translator({
                    id: typeKeyName,
                    defaultMessage: typeKeyName
                });
            };

            const overrides = drivers.map((driver, index) => {
                return {
                    [`nameCell${index}`]: {
                        content: _.startCase(_.toLower(getFullName(driver.person)))
                    },
                    [`nameCellStatus${index}`]: {
                        visible: _.isString(driver.error)
                    },
                    [`dobCell${index}`]: {
                        content: driver.dateOfBirth ? getFormattedDateString(driver.dateOfBirth) : ''
                    },
                    [`genderCell${index}`]: {
                        content: driver.gender ? getTypeKeyDescription(genderTypeList.getCode(driver.gender).name) : ''
                    },
                    [`maritalStatusCell${index}`]: {
                        content: driver.maritalStatus_ML ? getTypeKeyDescription(maritalStatusTypelist.getCode(driver.maritalStatus_ML).name) : ''
                    },
                    [`relationshipToInsuredCell${index}`]: {
                        content: driver.relationshipToInsured_ML ? getTypeKeyDescription(relationTypeList.getCode(driver.relationshipToInsured_ML).name) : ''
                    },
                    [`reasonNotListedCell${index}`]: {
                        content: driver.status_ML ? getTypeKeyDescription(statusTypeList.getCode(driver.status_ML).name) : ''
                    },
                    [`editActionCell${index}`]: {
                        content: translator(messages.edit),
                        'aria-labelledby': `nameCellContent${index} dobCellContent${index} genderCellContent${index} maritalStatusCellContent${index}`
                    },
                    [`removeActionCell${index}`]: {
                        content: translator(messages.remove),
                        'aria-labelledby': `nameCellContent${index} dobCellContent${index} genderCellContent${index} maritalStatusCellContent${index}`
                    }
                };
            });
            return Object.assign({}, ...overrides);
        }
        return {};
    };


    const getAddDriverModalHeader = () => {
        if (isAMP) {
            return translator(messages.addNewDriver)
        } 
        return driverType === 'rated' ? translator(messages.addDriver) : translator(messages.addExcludedDriver)
    };

    const addDriverHandler = () => {
        const polState = _.get(submissionVM, 'baseData.policyAddress.state.value.code');
        const excludedAndRequiresMVR = driverType === 'excluded' && _.includes(['MO','AK','OK'], polState) && !isAMP;

        if (driverType === 'rated' || excludedAndRequiresMVR) {
            const componentProps = {
                title: getAddDriverModalHeader(),
                overrideProps: {
                    submissionVM,
                    driverType,
                    onAddDriver,
                    onCancel,
                    viewModelService,
                    authHeader,
                    showProgressDialog,
                    EndorsementService
                },
                CustomComponent: MLDriverChangeAddDriverPopup,
                showFooterOnCustomComponent: false,
                additionalPEStyle: 'customFooterModal'
            };
    
            modalApi.showModal(
                <FormModalPopover {...componentProps} />
            );
        } else {
            onAddDriver(onCancel)
        }
    }

    const overrides = {
        contentContainer: {
            className: isAMP ? 'amp-ml-driver-table-popup' : 'ml-driver-table-popup'
        },
        householdMemberTableContainer: {
            visible: driverType === 'household' 
        },
        householdHeaderMemberTip: {
            content: getHeaderContent()
        },
        householdMemberTable: {
            visible: !_.isEmpty(drivers) && drivers.length > 0 
        },
        householdMemberIterable: {
            data: drivers
        },
        driverTableContainer: {
            visible: driverType === 'rated' 
        },
        driverTable: {
            visible: !_.isEmpty(drivers) && drivers.length > 0 
        },
        driverIterable: {
            data: drivers
        },
        excludedDriverTableContainer: {
            visible: driverType === 'excluded' 
        },
        excludedDriverHeaderMsg : {
            content: isModernProduct ? translator(_.isEmpty(drivers)  ? messages.noExcludedNonDrivers : messages.belowNonDriversAreExcluded) : translator(_.isEmpty(drivers)  ? messages.noExcludedMembers : messages.belowAreExcluded)
        },
        householdMemberHeaderMsg: {
            content: isModernProduct ? translator(messages.nonDriverHeader) : translator(messages.householdMemberHeader)
        },
        excludedDriverTip: {
            content: getHeaderContent()
        },
        excludedDriverTooltipContainer: {
            visible: isAMP
        },
        excludedDriverTable: {
            visible: !_.isEmpty(drivers) && drivers.length > 0 
        },
        excludedDriverIterable: {
            data: drivers
        },
        footer: {
            className: isAMP ? 'footer amp-footer-padding col-md-12' : 'footer col-md-12'
        },
        addBtn : {
            type: isGPA ? 'outlined' : 'filled',
            className: isGPA ? '' : styles['amp-footer-button'],
            content: {
                defaultMessage: translator(_.upperCase(getAddButtonLabel()))
            }
        },

        ...generateDriverOverrides()
    };

    const resolvers = {
        resolveCallbackMap : {
            addDriver: () => {addDriverHandler()},
            editDriver: (callbackProps) => {onEditDriver(drivers[callbackProps.index], onCancel)},
            removeDriver: (callbackProps) => {onRemoveDriver(drivers[callbackProps.index], onCancel)}
        },
        resolveClassNameMap: styles
    };

    return (
        <MetadataContent
            uiProps={metadata.componentContent}
            overrideProps={overrides}
            {...resolvers} />
    );
};

MLDriverChangeTablePopup.propTypes = {
    drivers: PropTypes.array.isRequired,
    onAddDriver: PropTypes.func.isRequired,
    onEditDriver: PropTypes.func.isRequired,
    onRemoveDriver: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
};

MLDriverChangeTablePopup.defaultProps = {
    driverList:[]
}

export default MLDriverChangeTablePopup;
